import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { api } from '@/services/api'
import { groupMessageLang } from '@/lib/group-form-message'

interface GenerateGroupFormParams {
    reservaId: string
    force?: boolean
}

interface GroupFormResponse {
    success: boolean
    url: string
    grup: {
        habitacions: number
        viatgers: number
        registrats: number
        idioma: string | null
    }
}

/**
 * Enllaç de grup (F8): un sol formulari per a totes les reserves del grup de Beds24. Copia al
 * porta-retalls el missatge per a l'hoste (WhatsApp o correu) amb l'enllaç, en el seu idioma.
 * Si el grup ja té un enllaç actiu, el backend torna el mateix.
 */
export function useGenerateGroupForm() {
    const queryClient = useQueryClient()
    const { i18n } = useTranslation()

    return useMutation({
        mutationFn: async ({ reservaId, force = false }: GenerateGroupFormParams) => {
            const response = await api.post<GroupFormResponse>('/forms/generar-formulari-reserva', {
                reserva_id: reservaId,
                grup: true,
                force,
            })
            return response.data
        },
        onSuccess: async (data) => {
            const lang = groupMessageLang(data.grup.idioma)
            const t = i18n.getFixedT(lang, 'guestPortal')
            const { habitacions, viatgers, registrats } = data.grup
            const message = t('group.shareMessage', { rooms: habitacions, guests: viatgers, url: data.url })

            queryClient.invalidateQueries({ queryKey: ['formularis'] })

            try {
                await navigator.clipboard.writeText(message)
                toast.success(`Missatge del grup copiat (${lang.toUpperCase()})`, {
                    description: `Un sol formulari per a ${habitacions} habitacions · ${viatgers} viatgers` +
                        (registrats > 0 ? ` (${registrats} ja registrats)` : ''),
                })
            } catch {
                toast.error("No s'ha pogut copiar el missatge", { description: data.url })
            }
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || "Error en generar l'enllaç de grup")
        },
    })
}
